import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, withModifiers as _withModifiers, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, reactive, watch, onUnmounted, computed, PropType } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'

import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { SubnetStatusService, DYNAMIC_STATUS, ScanMode, SubnetStatusItem, SubnetStatusListReq } from './subnet-status.service'
import { TerminalDiscoveryService, ScanModel } from '../../terminal-discovery.service'
import { ElMessage, ElMessageBox } from 'element-plus'
import EditSubnetDrawer from '../edit-subnet-drawer/edit-subnet-drawer.vue'
import InstalledTerminalDrawer from '../installed-terminal-drawer/installed-terminal-drawer.vue'
import UninstalledTerminalDrawer from '../uninstalled-terminal-drawer/uninstalled-terminal-drawer.vue'
import ErrorSubnetDialog from '../ErrorSubnetDialog.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import { formatTimeStamp } from '@/utils/time'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'


export default /*#__PURE__*/_defineComponent({
  __name: 'subnet-status',
  props: {
  hasPermission: {
    type: Boolean,
    default: true
  },
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  }
},
  emits: ['viewAll'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()

const props = __props

const emits = __emit

const injector = useReflectiveInjector([SubnetStatusService, TerminalDiscoveryService])
const service = injector.get(SubnetStatusService)
const terminalDiscoveryService = injector.get(TerminalDiscoveryService)

const tableRef = ref<any>()
const { refreshList } = useCmTableListRefresh(tableRef)
const editDrawerVisible = ref(false)
const currentRow = ref<SubnetStatusItem>(null)
const installedTerminalDrawerVisible = ref(false)
const uninstalledTerminalDrawerVisible = ref(false)
let statusInterval: any = null
const stopLoop = ref(false)
const totalCount = computed(() => tableRef.value?.total || 0)
const selectedIds = ref<string[]>([])
const visible = ref(false)
const form = reactive<{ subnet_address?: string; scan_status?: number; page: number; limit: number }>({
  subnet_address: '',
  scan_status: undefined,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
})
const failedScanList = ref([])
const formColumns = computed<ObjectFormItem<typeof form, 'scan_status'>>(() => {
  void locale.value
  return {
    scan_status: {
      colOpt: { style: 'flex:0 0 150px' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      label: t('subnetStatus.scanStatus'),
      type: FormType.Select,
      selectOpt: {
        clearable: true
      },
      dictData: [
        { label: t('scanStatusLabel.scanning'), value: 1 },
        { label: t('scanStatusLabel.completed'), value: 2 },
        { label: t('scanStatusLabel.fail'), value: 3 },
        { label: t('scanStatusLabel.timeout'), value: 4 },
      ],
      events: {
        change () {
          handleQuery()
        }
      }
    }
  }
})

const pageOption = {
  pageNumName: 'page',
  pageSizeName: 'limit'
}

const tableOpt = computed(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : undefined,
    columns: [
      { prop: 'subnet_address', label: t('subnetStatus.subnetAddress'), columnOpt: { minWidth: 140 } },
      { prop: 'scan_model', label: t('subnetStatus.scanAgent'), columnOpt: { minWidth: 140 }, formatter: (_row: any, value: number) => (value === 1 ? t('scanModeLabel.specified') : value === 2 ? t('scanModeLabel.auto') : '--') },
      { prop: 'installed_clients', label: t('subnetStatus.installedCount'), columnOpt: { minWidth: 140 } },
      { prop: 'uninstalled_clients', label: t('subnetStatus.uninstalledCount'), columnOpt: { minWidth: 140 } },
      { prop: 'scan_status', label: t('subnetStatus.scanStatus'), columnOpt: { minWidth: 110 } },
      { prop: 'last_scan_time', label: t('subnetStatus.lastScanTime'), columnOpt: { minWidth: 140 }, formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm') },
      { prop: 'remarks', label: t('subnetStatus.remarks'), showOverflow: true, columnOpt: { minWidth: 140 } }
    ],
    rowKey: 'id',
    showActionBar: false,
    showSearch: false,
    showRightToolbar: true,
    selection: true,
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    path: 'list',
    totalPath: 'total',
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100
  }
})

const scanStatusLabelMap = computed(() => {
  void locale.value
  return {
    0: t('scanStatusLabel.notScanned'),
    1: t('scanStatusLabel.scanning'),
    2: t('scanStatusLabel.completed'),
    3: t('scanStatusLabel.fail'),
    4: t('scanStatusLabel.timeout')
  }
})

const tableActOpt = computed(() => {
  void locale.value
  return {
    columnOpt: { label: t('commonPage.operation'), width: 140, fixed: 'right' },
    actionButtons: () => [
      { name: t('subnetStatus.edit'), type: 'edit', opt: { size: 'small', type: 'text' }, disabled: () => !props.hasPermission },
      { name: t('subnetStatus.scanNow'), type: 'scan', opt: { size: 'small', type: 'text' }, disabled: () => !props.hasPermission }
    ]
  }
})

function getListApi(params: any) {
  const req: SubnetStatusListReq = {
    subnet_address: params.subnet_address ?? '',
    limit: params.limit || (props.viewMode === TableViewMode.preview ? 10 : 20),
    page: params.page || 1
  }
  if (params.scan_status !== undefined && params.scan_status !== null && params.scan_status !== '') {
    req.scan_status = Number(params.scan_status)
  }
  if (params.sort) {
    req.sort = params.sort
    req.asc = params.asc !== undefined ? params.asc : false
  }
  return service.getSubnetStatusList(req)
}

function handleViewAll() {
  emits('viewAll')
}

function handleSelectionChange(selection: any[]) {
  selectedIds.value = selection.map((item: any) => String(item.id))
}

function handleBatchScan() {
  ElMessageBox.confirm(t('terminalDiscoveryExtra.confirmScanSelected', { count: selectedIds.value.length }), t('cmtable.tip')).then(() => {
    terminalDiscoveryService.scanSubnet({ scan_model: ScanModel.batch, ids: selectedIds.value }).then((res) => {
      ElMessage.success(t('subnetStatus.scanTaskSent'))
      selectedIds.value = []
      visible.value = !!res?.not_online_device_subnets.length
      failedScanList.value = res?.not_online_device_subnets || []
      refreshData()
    })
  })
}

function handleAction({ type, rowItem }: { type: string, rowItem: any }) {
  if (type === 'edit') {
    handleEdit(rowItem)
  } else if (type === 'scan') {
    handleScan(rowItem)
  }
}

function handleEdit(row: any) {
  currentRow.value = row
  editDrawerVisible.value = true
}

function handleScan(row: any) {
  ElMessageBox.confirm(t('subnetStatus.confirmScanSubnet'), t('cmtable.tip'), {
    type: 'warning'
  }).then(() => {
    terminalDiscoveryService.scanSubnet({ scan_model: ScanModel.batch, ids: [String(row.id)] }).then((res) => {
      !!res?.not_online_device_subnets.length ? ElMessage.error(t('subnetStatus.noOnlineAgent')) : ElMessage.success(t('subnetStatus.scanTaskSent'))
      refreshData()
    })
  })
}

function handleViewTerminals(row: any, type: 'installed' | 'uninstalled') {
  currentRow.value = row
  if (!props.hasPermission) return;
  if (type === 'installed') {
    installedTerminalDrawerVisible.value = true
  } else {
    uninstalledTerminalDrawerVisible.value = true
  }
}

function handleSubnetSave() {
  editDrawerVisible.value = false
  refreshData()
}

async function handleSearch(value: string) {
  form.subnet_address = value
  await refreshList()
}

async function handleQuery() {
  await refreshList()
}

function handleReset() {
  form.subnet_address = ''
  form.scan_status = undefined
  void refreshList()
}

function refreshData() {
  void refreshList()
  startLoopStatus()
}

function startLoopStatus() {
  clearInterval(statusInterval)
  statusInterval = setInterval(async () => {
    const tableData = tableRef.value?.rowData?.list || []
    const hasScanning = tableData.find((v: any) => DYNAMIC_STATUS.includes(v.scan_status))
    if (hasScanning) {
      if (stopLoop.value) return
      stopLoop.value = true
      await refreshList()
      stopLoop.value = false
    } else {
      clearInterval(statusInterval)
    }
  }, 60000)
}

watch(
  () => props.viewMode,
  (vm) => {
    form.limit = vm === TableViewMode.preview ? 10 : 20
  }
)

const exportQueryParams = computed<Pick<SubnetStatusListReq, 'subnet_address'> & Partial<Pick<SubnetStatusListReq, 'scan_status'>>>(() => ({
  subnet_address: form.subnet_address || '',
  ...(form.scan_status !== undefined && form.scan_status !== null ? { scan_status: form.scan_status } : {})
}))

function download(type: 0 | 1) {
  service.download(type, exportQueryParams.value, t('subnetStatus.exportFileName'))
}

function openInstalledDrawerForRow(row: SubnetStatusItem) {
  if (!props.hasPermission || !row) return
  currentRow.value = row
  installedTerminalDrawerVisible.value = true
}

function openUninstalledDrawerForRow(row: SubnetStatusItem) {
  if (!props.hasPermission || !row) return
  currentRow.value = row
  uninstalledTerminalDrawerVisible.value = true
}

__expose({
  refreshData,
  openInstalledDrawerForRow,
  openUninstalledDrawerForRow,
})

onUnmounted(() => {
  clearInterval(statusInterval)
})

startLoopStatus()

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_link = _resolveComponent("el-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt.value,
      api: getListApi,
      "has-permission": __props.hasPermission,
      "page-option": pageOption,
      modelValue: form,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form) = $event)),
      onHandleAction: handleAction,
      onChangeSelected: handleSelectionChange
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          modelValue: form,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form) = $event)),
          "form-columns": formColumns.value,
          disabled: !__props.hasPermission,
          "show-search": true,
          "search-placeholder": _ctx.$t('subnetStatus.searchPlaceholder'),
          "popover-threshold": 0,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              size: "small",
              onClick: handleBatchScan,
              disabled: selectedIds.value.length === 0 || !__props.hasPermission
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('subnetStatus.startScan')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(ExportDropdown, {
              "has-permission": __props.hasPermission,
              onExport: download
            }, null, 8, ["has-permission"]),
            (props.viewMode === _unref(TableViewMode).preview)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  type: "primary",
                  size: "small",
                  plain: "",
                  onClick: handleViewAll,
                  disabled: !__props.hasPermission
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
      ]),
      scan_status: _withCtx(({ row }: any) => [
        _createElementVNode("span", null, _toDisplayString(scanStatusLabelMap.value[row.scan_status] ?? '--'), 1),
        _withDirectives(_createElementVNode("span", null, "...", 512), [
          [_vShow, _unref(DYNAMIC_STATUS).includes(row.scan_status)]
        ])
      ]),
      installed_clients: _withCtx(({ row }: any) => [
        _createVNode(_component_el_link, {
          disabled: !row.installed_clients || !__props.hasPermission,
          type: "primary",
          onClick: _withModifiers(($event: any) => (handleViewTerminals(row, 'installed')), ["prevent"]),
          underline: false
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(row.installed_clients), 1)
          ]),
          _: 2
        }, 1032, ["disabled", "onClick"])
      ]),
      uninstalled_clients: _withCtx(({ row }: any) => [
        _createVNode(_component_el_link, {
          disabled: !row.uninstalled_clients || !__props.hasPermission,
          type: "primary",
          onClick: _withModifiers(($event: any) => (handleViewTerminals(row, 'uninstalled')), ["prevent"]),
          underline: false
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(row.uninstalled_clients), 1)
          ]),
          _: 2
        }, 1032, ["disabled", "onClick"])
      ]),
      _: 1
    }, 8, ["table-opt", "table-act-opt", "has-permission", "modelValue"]),
    _createVNode(EditSubnetDrawer, {
      modelValue: editDrawerVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editDrawerVisible).value = $event)),
      "subnet-data": currentRow.value,
      onSave: handleSubnetSave
    }, null, 8, ["modelValue", "subnet-data"]),
    _createVNode(InstalledTerminalDrawer, {
      modelValue: installedTerminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((installedTerminalDrawerVisible).value = $event)),
      "subnet-data": currentRow.value,
      "has-permission": __props.hasPermission
    }, null, 8, ["modelValue", "subnet-data", "has-permission"]),
    _createVNode(UninstalledTerminalDrawer, {
      modelValue: uninstalledTerminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((uninstalledTerminalDrawerVisible).value = $event)),
      "subnet-data": currentRow.value,
      "has-permission": __props.hasPermission
    }, null, 8, ["modelValue", "subnet-data", "has-permission"]),
    _createVNode(ErrorSubnetDialog, {
      modelValue: visible.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((visible).value = $event)),
      "failed-scan-list": failedScanList.value
    }, null, 8, ["modelValue", "failed-scan-list"])
  ], 64))
}
}

})